<script setup lang="ts">
import AppLayout from '../layouts/AppLayout.vue'
import HealthStatus from '../components/HealthStatus.vue'
import SupplierLogo from '../components/SupplierLogo.vue'
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '../composables/useAuth'
import { getSupplierDashboard } from '../services/api'
import type { SupplierDashboard } from '../types/supplier'
import { useCurrentContext } from '../composables/useCurrentContext'

const router = useRouter()
const { supplierId } = useCurrentContext()
const { getAccessTokenSilently } = useAuth()

const loading = ref(true)
const error = ref<string | null>(null)
const dashboard = ref<SupplierDashboard | null>(null)

async function loadDashboard() {
  try {
    loading.value = true
    if (!supplierId.value) {
      throw new Error('No supplier context available')
    }
    const token = await getAccessTokenSilently()
    const data = await getSupplierDashboard(supplierId.value, token)
    dashboard.value = data
  } catch (e: any) {
    console.error(e)
    error.value = "Unable to load supplier dashboard data right now."
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadDashboard()
})

const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val)
}

const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString()
}

const goToNewPromotion = () => {
    router.push('/supplier/promotions?create=true')
}
</script>

<template>
  <AppLayout>
    <div class="space-y-8">
      <!-- Header -->
      <div v-if="dashboard" class="flex flex-col md:flex-row md:items-center justify-between gap-4">
         <div class="flex items-center gap-4">
            <SupplierLogo :src="dashboard.supplier?.logoUrl" :alt="dashboard.supplier?.name || 'Logo'" class="w-16 h-16 object-contain border rounded-lg bg-white p-2" />
            <div>
                <h1 class="text-3xl font-bold text-cc-text">{{ dashboard.supplier?.name }}</h1>
                <div class="flex items-center gap-2 mt-1">
                    <span class="text-xs text-cc-text-tertiary">Feed Synced: {{ dashboard.metrics?.suppliersTxtStatus?.lastSyncAt ? new Date(dashboard.metrics.suppliersTxtStatus.lastSyncAt).toLocaleTimeString() : 'N/A' }}</span>
                </div>
            </div>
         </div>
         <div>
           <router-link to="/supplier/profile" class="text-sm font-medium text-gray-600 hover:text-gray-900 mr-4">
             View Company Profile
           </router-link>
           <button @click="goToNewPromotion" class="bg-gray-900 text-white px-4 py-2 rounded-md hover:bg-gray-800 transition shadow-sm text-sm font-medium">
             + New Promotion
           </button>
         </div>
      </div>
      <div v-else-if="loading" class="h-20 bg-gray-100 rounded animate-pulse"></div>

      <!-- Health Check (keep visible for demo/debug) -->
      <HealthStatus />

      <!-- Error State -->
      <div v-if="error" class="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md">
          {{ error }}
      </div>

      <!-- Metrics Grid -->
      <div v-if="dashboard" class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <MetricTile 
            v-for="tile in dashboard.tiles" 
            :key="tile.id"
            :label="tile.label" 
            :value="tile.value"
            :color="tile.color"
            :title="tile.description"
        />
        
        <!-- Empty State Tile -->
        <router-link 
            v-if="dashboard.tiles && dashboard.tiles.length < 8"
            to="/supplier/metrics"
            class="bg-cc-deep p-4 rounded-lg border-2 border-dashed border-cc-border flex flex-col justify-center items-center h-full hover:border-gray-300 dark:hover:border-gray-600 transition group"
        >
            <span class="text-2xl mb-1 text-gray-400 group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300">+</span>
            <span class="text-xs font-medium text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-200">Add Metric</span>
        </router-link>
      </div>
      <div v-else-if="loading" class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div v-for="i in 6" :key="i" class="h-24 bg-gray-100 rounded animate-pulse"></div>
      </div>
      
      <!-- Link to Metrics Catalog -->
      <div v-if="dashboard" class="text-center">
          <router-link to="/supplier/metrics" class="text-indigo-600 hover:text-indigo-800 text-sm font-medium hover:underline">
              Want to see more? View all 50+ metrics →
          </router-link>
      </div>

      <!-- Top Deals Table -->
      <div class="bg-cc-elevated rounded-lg shadow-sm border border-cc-border overflow-hidden">
        <div class="px-6 py-4 border-b border-cc-border bg-gray-50 dark:bg-gray-700/50 flex justify-between items-center">
             <h3 class="font-bold text-gray-700 dark:text-white">Top Deals (Closed Won)</h3>
             <span class="text-xs text-gray-400">Last 90 Days</span>
        </div>
        
        <div v-if="loading" class="p-8 text-center text-gray-400">Loading deals...</div>
        
        <div v-else-if="dashboard && dashboard.topDeals?.length > 0" class="overflow-x-auto">
            <table class="w-full text-sm text-left">
                <thead class="text-xs text-gray-500 uppercase bg-gray-50 dark:bg-gray-700/50 border-b border-cc-border">
                    <tr>
                        <th class="px-6 py-3 dark:text-gray-400">Deal Name</th>
                        <th class="px-6 py-3 dark:text-gray-400">Advisor</th>
                        <th class="px-6 py-3 text-right dark:text-gray-400">MRR</th>
                        <th class="px-6 py-3 text-right dark:text-gray-400">NRR</th>
                        <th class="px-6 py-3 dark:text-gray-400">Date</th>
                        <th class="px-6 py-3 dark:text-gray-400">Products</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="deal in dashboard.topDeals" :key="deal.id" class="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition">
                        <td class="px-6 py-4 font-medium text-cc-text">{{ deal.dealName }}</td>
                        <td class="px-6 py-4 text-cc-text-secondary">{{ deal.advisorName }}</td>
                        <td class="px-6 py-4 text-right font-medium text-green-700 dark:text-green-400">{{ formatCurrency(deal.amountMrr) }}</td>
                        <td class="px-6 py-4 text-right text-cc-text-secondary">{{ formatCurrency(deal.amountNrr) }}</td>
                        <td class="px-6 py-4 text-cc-text-secondary">{{ formatDate(deal.dealDate) }}</td>
                        <td class="px-6 py-4 text-cc-text-secondary truncate max-w-xs" :title="deal.summaryProducts.join(', ')">
                            {{ (deal.summaryProducts || []).slice(0, 2).join(', ') }}
                            <span v-if="(deal.summaryProducts || []).length > 2" class="text-xs bg-cc-hover rounded px-1 text-cc-text-secondary">+{{ (deal.summaryProducts || []).length - 2 }}</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        
        <div v-else-if="dashboard" class="p-12 text-center text-gray-500">
            No closed deals found for this period.
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script lang="ts">
// Simple internal component for tiles
import { defineComponent, h } from 'vue'
const MetricTile = defineComponent({
    props: ['label', 'value', 'color'],
    render() {
        return h('div', { class: 'bg-cc-elevated p-4 rounded-lg shadow-sm border border-cc-border flex flex-col justify-between h-full' }, [
            h('div', { class: 'text-cc-text-tertiary text-xs uppercase tracking-wider font-semibold mb-1' }, this.label),
            h('div', { class: `text-2xl font-bold ${this.color || 'text-cc-text'}` }, this.value)
        ])
    }
})
</script>
